import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  useLabelProjectMappings,
  useUpsertLabelProjectMapping,
  useDeleteLabelProjectMapping,
} from '../../hooks/useLabelProjectMappings';
import { useLeadLabels } from '../../hooks/useLeads';
import { getProjects } from '../../services/api/projects';
import type { ProjectListItem } from '../../services/api/projects';
import type { LabelProjectMapping } from '../../services/api/leadLabelMappings';
import { labelTextColor } from '../../utils/labelColor';

function ProjectPicker({
  labelId,
  currentMapping,
  projects,
}: {
  labelId: number | null;
  currentMapping: LabelProjectMapping | undefined;
  projects: ProjectListItem[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const upsert = useUpsertLabelProjectMapping();
  const remove = useDeleteLabelProjectMapping();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = projects
    .filter((p) => p.state === 'in_progress')
    .filter((p) => (p.fullName || p.name || '').toLowerCase().includes(filter.toLowerCase()))
    .slice(0, 20);

  const currentProjectName = currentMapping?.project?.fullName ?? currentMapping?.project?.name ?? null;
  const displayValue = isOpen ? filter : (currentProjectName ?? '');

  return (
    <div className="flex items-center gap-2">
      <div className="relative min-w-64 flex-1 max-w-xl" ref={dropdownRef}>
        <input
          type="text"
          value={displayValue}
          onChange={(e) => {
            setFilter(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            setFilter('');
            setIsOpen(true);
          }}
          placeholder="Select a project..."
          className="w-full rounded-lg border border-gray-300 px-3 py-1.5 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
        >
          <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-auto">
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  upsert.mutate({ labelId, projectId: p.id });
                  setFilter('');
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                  currentMapping?.project?.id === p.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
              >
                {p.fullName || p.name}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-sm text-gray-400">No projects found</p>
            )}
          </div>
        )}
      </div>
      {currentMapping && (
        <button
          type="button"
          onClick={() => remove.mutate(labelId)}
          disabled={remove.isPending}
          className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
        >
          Clear
        </button>
      )}
    </div>
  );
}

export default function LabelProjectMappingsPage() {
  const { data: labelsData, isLoading: labelsLoading } = useLeadLabels();
  const { data: mappingsData, isLoading: mappingsLoading } = useLabelProjectMappings();
  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProjects(),
  });

  const labels = labelsData?.data ?? [];
  const mappings = mappingsData?.data ?? [];

  const fallbackMapping = mappings.find((m) => m.labelId === null);
  const mappingByLabelId = Object.fromEntries(
    mappings.filter((m) => m.label !== null).map((m) => [m.label!.id, m]),
  );

  const isLoading = labelsLoading || mappingsLoading || projectsLoading;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900">Presales Project Mappings</h1>
      <p className="text-sm text-gray-500">
        Map each lead label to a parent project. When a lead with that label is created, a presales
        sub-project will be automatically created under the mapped parent project.
      </p>
      <p className="text-sm text-gray-500">
        The pinned &quot;No label&quot; row below sets a fallback parent used when a lead has no
        labels, or none of its labels have a mapping.
      </p>

      <div className="bg-white rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase rounded-tl-lg">Label</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase rounded-tr-lg">
                Mapped Parent Project
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            <tr>
              <td className="px-6 py-3">
                <span className="inline-block rounded px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600">
                  No label / fallback
                </span>
              </td>
              <td className="px-6 py-3">
                <ProjectPicker labelId={null} currentMapping={fallbackMapping} projects={projects} />
              </td>
            </tr>
            {isLoading && (
              <tr>
                <td colSpan={2} className="px-6 py-4 text-sm text-gray-400">
                  Loading...
                </td>
              </tr>
            )}
            {!isLoading && labels.length === 0 && (
              <tr>
                <td colSpan={2} className="px-6 py-4 text-sm text-gray-400">
                  No lead labels configured yet.
                </td>
              </tr>
            )}
            {labels.map((label) => (
              <tr key={label.id}>
                <td className="px-6 py-3">
                  <span
                    className="inline-block rounded px-2 py-0.5 text-xs font-medium"
                    style={{ backgroundColor: label.color, color: labelTextColor(label.color) }}
                  >
                    {label.name}
                  </span>
                </td>
                <td className="px-6 py-3">
                  <ProjectPicker
                    labelId={label.id}
                    currentMapping={mappingByLabelId[label.id]}
                    projects={projects}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
